/** @file useBulkUpload: the bulk-upload flow of the Art screen: unpack, plan, ask for a fit, fill, report. */
import type { ImageSlot } from '@zilo/pose3d-format/types/facet';
import { useState } from 'react';
import type { ArtActions } from './artActions';
import type { BulkOutcome, BulkPlan, FitMode } from './artTypes';
import { planBulkUpload } from './planBulkUpload';
import { unpackUpload } from './unpackUpload';

/** The flow's state and steps: `pending` waits for Pad / Scale / Skip, `result` is the report to show. */
export type BulkUpload = {
  pending: BulkPlan | null; result: BulkOutcome | null; busy: boolean;
  start: (files: readonly File[]) => Promise<void>;
  choose: (fit: FitMode | null) => Promise<void>;
  closeResult: () => void;
};

/** Files come in through `start`; a size mismatch pauses on `pending`, then every fill goes out in one batch. */
export function useBulkUpload(actions: Pick<ArtActions, 'fillMany'>, slots: readonly ImageSlot[], onError: (text: string) => void): BulkUpload {
  const [pending, setPending] = useState<BulkPlan | null>(null);
  const [result, setResult] = useState<BulkOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (plan: BulkPlan, fit: FitMode | null): Promise<void> => {
    setBusy(true);
    try {
      const fitted = fit === null ? [] : plan.misfits.map(({ size: _size, slotSize: _slotSize, ...image }) => ({ ...image, fit }));
      const left = fit === null ? plan.misfits.map((m) => ({ name: m.name, reason: `${m.size[0]}x${m.size[1]} where the slot is ${m.slotSize[0]}x${m.slotSize[1]}` })) : [];
      const outcome = await actions.fillMany([...plan.ready, ...fitted]);
      setResult({ filled: outcome.filled, skipped: [...plan.skipped, ...left, ...outcome.skipped] });
    } finally {
      setBusy(false);
    }
  };
  const start = async (files: readonly File[]): Promise<void> => {
    if (files.length === 0) return;
    let plan: BulkPlan;
    try {
      plan = planBulkUpload(await unpackUpload(files), slots);
    } catch (e) {
      onError(`Upload: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    if (plan.misfits.length > 0) setPending(plan);
    else await run(plan, null);
  };
  const choose = async (fit: FitMode | null): Promise<void> => {
    const plan = pending;
    setPending(null);
    if (plan !== null) await run(plan, fit);
  };
  return { pending, result, busy, start, choose, closeResult: () => { setResult(null); } };
}
